/**
 * ContractorPaymentFinalise · iter303ak-fix33 — Job Completed · T20 · Action 10
 * ──────────────────────────────────────────────────────────────────────────
 * Per accepted-child × contractor:
 *   · Tag each Extras item to a contractor (dropdown)
 *   · Show base labour (from contractor-overview)
 *   · Show extras attributed (sum of tagged extras)
 *   · Apply QI rating multiplier (Excellent 105 · Good 100 · Needs Fix 70 · Poor 50)
 *     — operator clicks "Apply suggested split" to populate the total
 *   · MOCKED "Send payment confirmation" per row → generates the
 *     Payment Confirmation PDF and stamps contractor_payment_sent_at
 *
 * Persists parent.contractor_payments + parent.extras_contractor_tags +
 * parent.contractor_payment_sent_at.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { sendWorkflowEmail, signOff } from './_emailSend';
import { CheckCircle2, DollarSign, Send, AlertTriangle, Wallet, User, ExternalLink } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const pdfUrl = (child_id, contractor_id) => {
  const base = `${BACKEND}/api/reports/payment-confirmation/${child_id}?contractor_id=${contractor_id}&inline=1`;
  return base;
};

// QI rating → payment multiplier (fix33 · Trevor's spec 1b with confirm).
const RATING_MULTIPLIERS = {
  'Excellent': 1.05,
  'Good':      1.00,
  'Needs Fix': 0.70,
  'Poor':      0.50,
};

const isEmailValid = (e) => !!(e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim()));
const nz = (n) => Number(n || 0).toFixed(2);

export default function ContractorPaymentFinalise({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentId = capturedData?.appraisal_id;
  const stampFieldPrefix = params.stamp_field_prefix || 'contractor_payment_sent_at';

  // Read QI rating + per-area ratings (fix34 — per-area drives per-line multiplier).
  const parent = useMemo(() => (
    capturedData?.appraisal_record || capturedData?.appraisal || {}
  ), [capturedData]);
  const qi = useMemo(() => parent.quality_inspection || {}, [parent]);
  const qiRating = qi.rating || '';
  const qiPerArea = useMemo(() => qi.per_area_ratings || {}, [qi]);   // {child_id:{component_type:{rating,notes,photo_ids[]}}}
  const overallMultiplier = RATING_MULTIPLIERS[qiRating] ?? 1.00;
  const qiFlagged = qiRating === 'Needs Fix' || qiRating === 'Poor';

  // Accepted children + their contractor rows.
  const acceptedChildren = useMemo(() => {
    const decisions = parent.child_decisions || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [parent]);

  // Resolve the multiplier for a single line (Q5b — per-area rating drives
  // per-line pay). Falls back to overall rating when the area is unrated.
  const lineMultiplier = useCallback((child_id, component_type) => {
    const areaRating = qiPerArea?.[child_id]?.[component_type]?.rating;
    if (areaRating && RATING_MULTIPLIERS[areaRating]) return RATING_MULTIPLIERS[areaRating];
    return overallMultiplier;
  }, [qiPerArea, overallMultiplier]);

  const [rows, setRows] = useState([]);            // { child_id, appraisal_no, qtype_ref, variant_label, contractor_id, contractor_name, email, base_raw, base_adjusted, area_breakdown: [{component_type, share, mult, adjusted}] }
  const [tags, setTags] = useState(() => value?.extras_contractor_tags || parent.extras_contractor_tags || {});  // { extra_id: {child_id, contractor_id} }
  const [totals, setTotals] = useState(() => value?.contractor_payments || parent.contractor_payments || {});    // {child_id:{contractor_id:{base, extras_amount, multiplier, total, ...}}}
  const [sendState, setSendState] = useState(() => value?.send_state || {});
  const [busyKeys, setBusyKeys] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [globalErr, setGlobalErr] = useState('');
  const [previewKey, setPreviewKey] = useState(null);

  const extras = useMemo(() => {
    return parent.work_execution?.extras || [];
  }, [parent]);

  // Load contractor list + line-level base labour (per-area aware).
  const load = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true);
    const list = [];
    for (const c of acceptedChildren) {
      try {
        const { data: overview } = await api.get(`/appraisals/${c.child_id}/contractor-overview`);
        // Walk sections → aggregate per assigned contractor with per-line
        // multiplier applied. Sections without an assigned contractor
        // still fall through to Trevor's manual dropdown fill flow — we
        // skip them here.
        const perContractor = {};   // {cid: {name, email, area_breakdown: {component_type:{share,mult}}, base_raw, base_adjusted}}
        for (const sec of (overview?.sections || [])) {
          const cid = sec?.assigned_contractor_id;
          if (!cid) continue;
          if (!perContractor[cid]) {
            perContractor[cid] = {
              area_breakdown: {},
              base_raw: 0,
              base_adjusted: 0,
            };
          }
          const pc = perContractor[cid];
          for (const ln of (sec?.lines || [])) {
            if (ln?.excluded_from_report) continue;
            const share = Number(ln?.contractor_share || 0);
            const compType = ln?.component || sec?.contractor_type || 'Other';
            const mult = lineMultiplier(c.child_id, compType);
            const adjusted = share * mult;
            pc.base_raw += share;
            pc.base_adjusted += adjusted;
            const bucket = pc.area_breakdown[compType] || { share: 0, adjusted: 0, mult, count: 0 };
            bucket.share += share;
            bucket.adjusted += adjusted;
            bucket.mult = mult;
            bucket.count += 1;
            pc.area_breakdown[compType] = bucket;
          }
        }
        for (const [cid, pc] of Object.entries(perContractor)) {
          let contractor_name = '';
          let email = '';
          try {
            const { data: cdoc } = await api.get(`/contractors/${cid}`);
            contractor_name = cdoc?.contractor_name || cdoc?.company_name || '';
            email = cdoc?.email || '';
          } catch { /* silent */ }
          list.push({
            child_id: c.child_id,
            appraisal_no: c.appraisal_no,
            qtype_ref: c.quote_type_ref,
            variant_label: c.variant_label,
            contractor_id: cid,
            contractor_name,
            email,
            base_raw: Number(pc.base_raw.toFixed(2)),
            base_adjusted: Number(pc.base_adjusted.toFixed(2)),
            area_breakdown: Object.entries(pc.area_breakdown).map(([ct, b]) => ({
              component_type: ct,
              share: Number(b.share.toFixed(2)),
              adjusted: Number(b.adjusted.toFixed(2)),
              mult: b.mult,
              count: b.count,
            })).sort((a, b) => a.component_type.localeCompare(b.component_type)),
          });
        }
      } catch { /* silent */ }
    }
    setRows(list);
    setLoading(false);
  }, [acceptedChildren, lineMultiplier]);

  useEffect(() => { load(); }, [load]);

  // Broadcast state changes upstream so re-open re-hydrates.
  useEffect(() => {
    if (onChange) onChange({
      extras_contractor_tags: tags,
      contractor_payments: totals,
      send_state: sendState,
    });
  }, [tags, totals, sendState]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Extras attributed per contractor.
  const extrasByContractor = useMemo(() => {
    const map = {};
    for (const x of extras) {
      const tag = tags[x.id];
      if (!tag?.child_id || !tag?.contractor_id) continue;
      const key = `${tag.child_id}|${tag.contractor_id}`;
      map[key] = (map[key] || 0) + Number(x.cost || 0);
    }
    return map;
  }, [extras, tags]);

  const setTag = (extra_id, patch) => setTags(prev => ({
    ...prev,
    [extra_id]: { ...(prev[extra_id] || {}), ...patch },
  }));

  const setTotalField = (child_id, contractor_id, patch) => {
    setTotals(prev => ({
      ...prev,
      [child_id]: {
        ...(prev[child_id] || {}),
        [contractor_id]: { ...((prev[child_id] || {})[contractor_id] || {}), ...patch },
      },
    }));
  };

  const applySuggested = (row) => {
    // Per-area math: base_adjusted already applies each area's multiplier.
    // Extras are tagged extras and are paid at 100% (no QI-multiplier
    // applied to operator-added extras).
    const key = `${row.child_id}|${row.contractor_id}`;
    const extras_amount = Number(extrasByContractor[key] || 0);
    const effectiveMult = row.base_raw > 0
      ? Number((row.base_adjusted / row.base_raw).toFixed(4))
      : overallMultiplier;
    const total = row.base_adjusted + extras_amount;
    setTotalField(row.child_id, row.contractor_id, {
      base: row.base_adjusted,
      base_raw: row.base_raw,
      extras_amount,
      multiplier: effectiveMult,
      area_breakdown: row.area_breakdown,
      total: Number(total.toFixed(2)),
    });
  };

  const applyAllSuggested = () => {
    for (const r of rows) applySuggested(r);
  };

  const getRowTotal = (child_id, contractor_id) => (totals[child_id] || {})[contractor_id] || {};

  const isSent = (key) => !!sendState[key]?.sent_at;

  const sendOne = async (row) => {
    const key = `${row.child_id}|${row.contractor_id}`;
    const rt = getRowTotal(row.child_id, row.contractor_id);
    if (!rt.total && rt.total !== 0) {
      setSendState(prev => ({ ...prev, [key]: { error: 'Apply the suggested split (or enter a total) before sending.' } }));
      return;
    }
    if (!isEmailValid(row.email)) {
      setSendState(prev => ({ ...prev, [key]: { error: 'Contractor email invalid — edit on the contractor record.' } }));
      return;
    }
    setBusyKeys(prev => { const n = new Set(prev); n.add(key); return n; });
    setGlobalErr('');
    try {
      const nowIso = new Date().toISOString();
      if (parentId) {
        const existing = parent[stampFieldPrefix] || {};
        // Persist totals + tags + stamp in one PATCH.
        await api.patch(`/appraisals/${parentId}`, {
          contractor_payments: {
            ...(parent.contractor_payments || {}),
            ...totals,
          },
          extras_contractor_tags: tags,
          [stampFieldPrefix]: {
            ...existing,
            [row.child_id]: {
              ...(existing[row.child_id] || {}),
              [row.contractor_id]: nowIso,
            },
          },
        });
      }
      await sendWorkflowEmail(api, {
        to: row.email.trim(),
        subject: `Payment confirmation – A${row.appraisal_no || ''} – Roofs To Remedy Ltd`,
        body: `Hi ${row.contractor_name || ''},\n\nPlease find your payment confirmation attached.${signOff}`,
        attachments: [{ route: `/reports/payment-confirmation/${row.child_id}?contractor_id=${row.contractor_id}`, filename: `Payment_Confirmation_A${row.appraisal_no || row.child_id.slice(-6)}` }],
        appraisalId: row.child_id, event: 'contractor_payment_confirmation',
      });
      setSendState(prev => ({
        ...prev,
        [key]: { sent_at: nowIso, ok: true, to_email: row.email.trim() },
      }));
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e?.message || 'Send failed';
      setSendState(prev => ({ ...prev, [key]: { error: errMsg } }));
      setGlobalErr(errMsg);
    } finally {
      setBusyKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  const sendAll = async () => {
    for (const r of rows) {
      const key = `${r.child_id}|${r.contractor_id}`;
      if (isSent(key)) continue;
      const rt = getRowTotal(r.child_id, r.contractor_id);
      if (!rt.total && rt.total !== 0) continue;
      if (!isEmailValid(r.email)) continue;
      // eslint-disable-next-line no-await-in-loop
      await sendOne(r);
    }
  };

  const untagged = extras.filter(x => !tags[x.id]?.contractor_id);
  const allSent = rows.length > 0 && rows.every(r => isSent(`${r.child_id}|${r.contractor_id}`));
  const sentCount = rows.filter(r => isSent(`${r.child_id}|${r.contractor_id}`)).length;

  return (
    <div className="space-y-4" data-testid="wf-cpf">
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex items-start gap-3">
          <Wallet className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">Contractor Payment</div>
            <div className="text-sm mt-1 text-amber-800">
              {params.prompt || 'Tag each extra, apply the rating-driven split, then email the Payment Confirmation PDF to each contractor.'}
            </div>
          </div>
        </div>
        <div className="mt-3 p-2 rounded bg-white border border-amber-200 text-[11px] text-amber-800">
          Sends a real email with the payment confirmation PDF attached.
        </div>
      </div>

      {/* QI rating banner — now per-area aware. Shows overall summary +
          note that per-area multipliers are applied at line level. */}
      <div className={`p-3 border rounded-lg text-sm ${qiFlagged ? 'bg-rose-50 border-rose-300 text-rose-800' : 'bg-slate-50 border-slate-200 text-slate-700'}`}
        data-testid="wf-cpf-qi-banner">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-widest">QI overall</span>
          <span className="font-semibold">{qiRating || '— not set —'}</span>
          <span className="text-xs">
            · overall split <b className="font-mono">{(overallMultiplier * 100).toFixed(0)}%</b>
          </span>
          <span className="ml-auto text-xs">
            Per-area ratings apply their own multiplier to each line.
            {qiFlagged && ' Verify fixes before sending payment.'}
          </span>
        </div>
      </div>

      {/* Extras tagging */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <DollarSign size={14} className="text-slate-600" />
          <span className="text-sm font-semibold text-slate-800">Extras — tag to contractor</span>
          <span className="ml-auto text-[11px] text-slate-500">
            {extras.length - untagged.length}/{extras.length} tagged
          </span>
        </div>
        {extras.length === 0 && (
          <div className="p-3 text-xs text-slate-500 italic">No extras captured on this job.</div>
        )}
        {extras.length > 0 && (
          <div className="divide-y divide-slate-100">
            {extras.map(x => {
              const tag = tags[x.id] || {};
              return (
                <div key={x.id} className="px-3 py-2 grid grid-cols-1 md:grid-cols-12 gap-2 items-center"
                  data-testid={`wf-cpf-extra-${x.id}`}>
                  <div className="md:col-span-5 min-w-0">
                    <div className="text-xs font-medium text-slate-800 truncate">{x.description || 'Extra work'}</div>
                    <div className="text-[10px] text-slate-500 truncate">{x.notes || ''}</div>
                  </div>
                  <div className="md:col-span-2 text-xs font-mono text-slate-800">${nz(x.cost)}</div>
                  <div className="md:col-span-5">
                    <select value={`${tag.child_id || ''}|${tag.contractor_id || ''}`}
                      onChange={(e) => {
                        const [child_id, contractor_id] = e.target.value.split('|');
                        setTag(x.id, { child_id, contractor_id });
                      }}
                      data-testid={`wf-cpf-extra-tag-${x.id}`}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded">
                      <option value="|">— tag to contractor —</option>
                      {rows.map(r => (
                        <option key={`${r.child_id}|${r.contractor_id}`} value={`${r.child_id}|${r.contractor_id}`}>
                          {r.qtype_ref || 'Quote'} · {r.contractor_name || r.contractor_id}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {loading && <div className="text-sm text-slate-500 italic p-3">Loading contractor rows…</div>}

      {!loading && rows.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No contractors assigned to any accepted child.
        </div>
      )}

      {/* Payment rows */}
      {rows.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2 flex-wrap">
            <User size={14} className="text-slate-600" />
            <span className="text-sm font-semibold text-slate-800">Contractor payment rows</span>
            <span className="text-[11px] text-slate-500">— <b>{sentCount}</b> of <b>{rows.length}</b> sent</span>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={applyAllSuggested}
                data-testid="wf-cpf-apply-all"
                className="px-2.5 py-1 rounded text-[11px] font-semibold text-sky-700 bg-white border border-sky-300 hover:bg-sky-50">
                Apply suggested split to all
              </button>
              <button onClick={sendAll} disabled={allSent || busyKeys.size > 0}
                data-testid="wf-cpf-send-all"
                className="px-2.5 py-1 rounded text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 inline-flex items-center gap-1 disabled:opacity-40">
                <Send size={11} /> {allSent ? 'All sent ✓' : (busyKeys.size > 0 ? 'Sending…' : 'Send all')}
              </button>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {rows.map(r => {
              const key = `${r.child_id}|${r.contractor_id}`;
              const rt = getRowTotal(r.child_id, r.contractor_id);
              const extras_amount = Number(extrasByContractor[key] || 0);
              const suggestedTotal = r.base_adjusted + extras_amount;
              const effectiveMult = r.base_raw > 0 ? (r.base_adjusted / r.base_raw) : overallMultiplier;
              const sent = isSent(key);
              const err = sendState[key]?.error;
              const busy = busyKeys.has(key);
              return (
                <div key={key} className={`p-3 ${sent ? 'bg-emerald-50/50' : ''}`}
                  data-testid={`wf-cpf-row-${r.child_id}-${r.contractor_id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-sm font-medium text-slate-800">
                      {[r.qtype_ref, r.variant_label].filter(Boolean).join(' · ')} — {r.contractor_name || r.contractor_id}
                    </span>
                    {r.appraisal_no && <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{r.appraisal_no}</span>}
                    {sent && <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><CheckCircle2 size={10} /> Sent</span>}
                    <button onClick={() => setPreviewKey(previewKey === key ? null : key)}
                      data-testid={`wf-cpf-preview-toggle-${r.child_id}-${r.contractor_id}`}
                      className="ml-auto px-2 py-0.5 rounded text-[11px] text-sky-700 bg-white border border-sky-300 hover:bg-sky-50 inline-flex items-center gap-1">
                      <ExternalLink size={11} /> {previewKey === key ? 'Hide PDF' : 'Preview PDF'}
                    </button>
                  </div>
                  {/* Per-area breakdown showing which multiplier applies where */}
                  {r.area_breakdown && r.area_breakdown.length > 0 && (
                    <div className="mb-2 pl-3 border-l-2 border-slate-200">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Per-area split</div>
                      <div className="flex flex-wrap gap-1.5">
                        {r.area_breakdown.map(b => (
                          <span key={b.component_type}
                            data-testid={`wf-cpf-area-chip-${r.child_id}-${r.contractor_id}-${b.component_type}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono ${
                              b.mult >= 1.05 ? 'bg-emerald-50 border-emerald-300 text-emerald-800' :
                              b.mult >= 1.00 ? 'bg-sky-50 border-sky-300 text-sky-800' :
                              b.mult >= 0.70 ? 'bg-amber-50 border-amber-300 text-amber-800' :
                              'bg-rose-50 border-rose-300 text-rose-800'}`}>
                            <b>{b.component_type}</b>
                            <span>${nz(b.share)} × {(b.mult * 100).toFixed(0)}% = ${nz(b.adjusted)}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 pl-3 border-l-2 border-slate-200 text-xs">
                    <div className="md:col-span-3">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Base labour <span className="text-slate-400">(adjusted)</span></div>
                      <div className="font-mono text-slate-800">${nz(r.base_adjusted)}</div>
                      {r.base_adjusted !== r.base_raw && (
                        <div className="text-[10px] text-slate-400 line-through font-mono">${nz(r.base_raw)}</div>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Extras</div>
                      <div className="font-mono text-slate-800">${nz(extras_amount)}</div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Blended mult</div>
                      <div className="font-mono text-slate-800">{(effectiveMult * 100).toFixed(0)}%</div>
                    </div>
                    <div className="md:col-span-3">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                        Total <span className="text-slate-400">(suggested ${nz(suggestedTotal)})</span>
                      </div>
                      <input type="number" step="0.01"
                        value={rt.total ?? ''}
                        onChange={(e) => setTotalField(r.child_id, r.contractor_id, {
                          base: r.base_adjusted, base_raw: r.base_raw, extras_amount,
                          multiplier: effectiveMult, area_breakdown: r.area_breakdown,
                          total: Number(e.target.value),
                        })}
                        disabled={sent}
                        placeholder={nz(suggestedTotal)}
                        data-testid={`wf-cpf-total-${r.child_id}-${r.contractor_id}`}
                        className="w-full px-2 py-1 border border-slate-300 rounded font-mono disabled:opacity-60" />
                    </div>
                    <div className="md:col-span-2 flex items-end gap-1">
                      <button onClick={() => applySuggested(r)} disabled={sent}
                        data-testid={`wf-cpf-apply-${r.child_id}-${r.contractor_id}`}
                        className="px-2 py-1 text-[11px] font-semibold text-sky-700 bg-white border border-sky-300 hover:bg-sky-50 rounded disabled:opacity-40">
                        Apply
                      </button>
                      <button onClick={() => sendOne(r)} disabled={sent || busy || rt.total === undefined || rt.total === null}
                        data-testid={`wf-cpf-send-${r.child_id}-${r.contractor_id}`}
                        className="px-2 py-1 text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded inline-flex items-center gap-1 disabled:opacity-40">
                        <Send size={11} /> {sent ? 'Sent' : (busy ? '…' : 'Send')}
                      </button>
                    </div>
                  </div>
                  {previewKey === key && (
                    <iframe src={pdfUrl(r.child_id, r.contractor_id)}
                      title={`Payment confirmation ${r.contractor_name}`}
                      data-testid={`wf-cpf-iframe-${r.child_id}-${r.contractor_id}`}
                      className="w-full mt-2 border border-slate-200 rounded"
                      style={{ height: 400 }} />
                  )}
                  <div className="text-[10px] mt-1 pl-3">
                    {sent && <span className="text-emerald-700">✓ Stamped {new Date(sendState[key].sent_at).toLocaleString()} → {sendState[key].to_email}</span>}
                    {err && <span className="text-rose-700">Error: {err}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {globalErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{globalErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!allSent}
            data-testid="wf-cpf-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
